import { useId } from 'react';
import { s, type SpacingToken } from '@/theme/tokens';
import { Box } from './Box';
import { Text } from './Text';
import { TextField } from './TextField';
import type { TextFieldProps } from './TextField';
import type { ReactElement } from 'react';

type SideLabelTextFieldProps = Omit<TextFieldProps, 'label'> & {
  label: string;
  labelWidth?: SpacingToken;
  labelAdornment?: ReactElement;
  endAdornment?: ReactElement;
};

export const SideLabelTextField = ({
  id,
  label,
  labelWidth = '4xl',
  labelAdornment,
  endAdornment,
  required,
  sx,
  ...rest
}: SideLabelTextFieldProps): ReactElement => {
  const fallbackId = useId();
  const resolvedId = id ?? fallbackId;
  const hasAdornment = Boolean(endAdornment);
  const resolvedLabelWidth =
    labelWidth === '4xl' ? `calc(${s('4xl')} + ${s('lg')})` : s(labelWidth);
  const mergedSx = sx
    ? ([{ '& .MuiInputBase-root': { width: '100%' } }, ...(Array.isArray(sx) ? sx : [sx])].filter(
        Boolean,
      ) as TextFieldProps['sx'])
    : ({ '& .MuiInputBase-root': { width: '100%' } } satisfies TextFieldProps['sx']);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: hasAdornment
          ? `${resolvedLabelWidth} minmax(0, 1fr) auto`
          : `${resolvedLabelWidth} minmax(0, 1fr)`,
        alignItems: 'center',
        columnGap: s('md'),
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: s('sm'),
        }}
      >
        <Text
          role="bodyStrong"
          component="label"
          htmlFor={resolvedId}
          sx={(theme) => ({
            fontSize: theme.typography.subtitle1.fontSize,
            lineHeight: theme.typography.subtitle1.lineHeight,
          })}
        >
          {label}
          {required ? ' *' : ''}
        </Text>
        {labelAdornment ? (
          <Box sx={{ fontSize: '0.85em', display: 'flex', ml: s('xs'), mr: s('md') }}>
            {labelAdornment}
          </Box>
        ) : null}
      </Box>
      <TextField {...rest} id={resolvedId} label={undefined} required={required} sx={mergedSx} />
      {hasAdornment ? (
        <Box sx={{ display: 'flex', alignItems: 'center', ml: s('sm') }}>{endAdornment}</Box>
      ) : null}
    </Box>
  );
};
